/**
 * Universal Notifications Hook
 * Manages WebSocket connection and notification handling for admin portal
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message, notification as antdNotification } from 'antd';
import { useWebSocket } from '../assets/utils/useWebSocket';
import {
  NOTIFICATION_EVENT_TYPES,
  STORAGE_KEYS,
} from '../types/notifications';
import {
  getNotifications,
  addNotification,
  markAsRead,
  markAllAsRead,
  getUnreadCount,
  getNotificationById,
  saveNotifications,
} from '../utils/notificationStorage';
import {
  getAdminNotificationTitle,
  getAdminNotificationDescription,
  handleNotificationClick,
  showNotificationToast,
  getCategoryFromEventType,
  getAdminNotificationPriority,
} from '../utils/notificationHandler';
import {
  getNotificationPriority,
  getPriorityLevel,
} from '../utils/notificationPriority';

/**
 * Main hook for universal notifications
 * Connects to WebSocket at app root and works across all pages
 */
export function useUniversalNotifications(options) {
  const {
    enabled = true,
    token,
    userId,
    onNotification,
    onError,
    onConnect,
    onDisconnect,
  } = options || {};

  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  // Save unread count to localStorage
  const saveUnreadCount = useCallback((count) => {
    try {
      localStorage.setItem(STORAGE_KEYS.UNREAD_COUNT, count.toString());
    } catch (error) {
      console.error('Error saving unread count to localStorage:', error);
    }
  }, []);
  
  const onNotificationRef = useRef(onNotification);
  const onErrorRef = useRef(onError);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);

  // Update refs when callbacks change
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onConnectRef.current = onConnect;
  }, [onConnect]);

  useEffect(() => {
    onDisconnectRef.current = onDisconnect;
  }, [onDisconnect]);

  // Load initial unread count
  useEffect(() => {
    if (enabled) {
      const count = getUnreadCount();
      setUnreadCount(count);
      
      // Load notifications from storage
      const stored = getNotifications();
      setNotifications(stored);
    }
  }, [enabled]);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((data) => {
    console.log('📨 WebSocket message received:', data);

    // Handle authentication response
    if (data.type === 'authenticated') {
      console.log('✅ WebSocket authenticated');
      console.log('User ID:', data.user_id);
      onConnectRef.current?.();
      return;
    }

    // Handle errors
    if (data.type === 'error') {
      console.error('❌ WebSocket error:', data.message);
      
      if (data.code === 'AUTH_FAILED') {
        // Redirect to login
        console.error('Authentication failed, redirecting to login');
        window.location.href = '/login';
      }
      
      const error = new Error(data.message || 'WebSocket error');
      onErrorRef.current?.(error);
      return;
    }

    // A role/permission change, not a user-visible notification — never
    // let it reach the notification list/bell. Dispatched as a plain
    // browser event rather than handled here directly because useAuth
    // (which owns refreshProfile and knows the current user's identity to
    // self-filter against) has no reason to depend on this hook, or vice
    // versa. Server payload shape is { event, notification: {...}, timestamp }
    // (see WebSocketServer::send), not { event, payload }, which is why this
    // reads data.notification explicitly rather than falling into the
    // generic payload-normalisation branch below.
    if (data.event === 'permissions_changed') {
      const { user_id: userId, user_type: userType } = data.notification || {};
      window.dispatchEvent(new CustomEvent('rbacPermissionsChanged', { detail: { userId, userType } }));
      return;
    }

    // Handle real notifications
    if (data.event || data.payload) {
      const eventType = data.event;
      const payload = data.payload || data;

      // Skip if event type is not recognized
      if (!eventType) {
        console.warn('Unknown notification event type:', data);
        return;
      }

      // Build notification object
      const notification = {
        id: data.id || payload.id || `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: eventType,
        event: eventType, // Keep both for compatibility
        title: data.title || payload.title || getAdminNotificationTitle(eventType, payload),
        body: data.body || payload.body || getAdminNotificationDescription(eventType, payload),
        priority: data.priority || payload.priority || getPriorityLevel(eventType),
        category: getCategoryFromEventType(eventType),
        isRead: false,
        read: false, // Keep for backward compatibility
        readAt: null,
        createdAt: data.timestamp 
          ? new Date(data.timestamp * 1000).toISOString()
          : payload.created_at 
            ? payload.created_at
            : new Date().toISOString(),
        actionable: data.actionable || payload.actionable,
        metadata: data.metadata || payload.metadata,
        payload: payload, // Store full payload for deep linking
        data: payload, // Alias for compatibility
      };

      console.log('🔔 New notification:', notification);

      // Store notification in localStorage
      addNotification(notification);

      // Update state
      setNotifications(prev => [
        notification,
        ...prev.slice(0, 99), // Keep max 100 notifications
      ]);

      // Update unread count and save to localStorage
      setUnreadCount(prev => {
        const newCount = prev + 1;
        saveUnreadCount(newCount); // Save to localStorage
        return newCount;
      });

      // Show toast notification
      const priorityConfig = getAdminNotificationPriority(eventType);
      antdNotification[priorityConfig.messageType]({
        message: notification.title,
        description: notification.body,
        placement: 'topRight',
        duration: priorityConfig.duration,
        onClick: () => {
          handleNotificationClick(notification, navigate);
        },
        key: notification.id, // Prevent duplicate toasts for same notification
      });

      // Trigger custom notification callback if provided
      if (onNotificationRef.current) {
        onNotificationRef.current(notification);
      }
    }
  }, [navigate, saveUnreadCount]);

  // Handle WebSocket connection
  const handleConnect = useCallback(() => {
    console.log('🔌 WebSocket connected');
    onConnectRef.current?.();
  }, []);

  // Handle WebSocket disconnection
  const handleDisconnect = useCallback(() => {
    console.log('🔌 WebSocket disconnected');
    onDisconnectRef.current?.();
  }, []);

  // Handle WebSocket errors
  const handleError = useCallback((error) => {
    // Don't trigger error callback for WebSocket connection failures
    // These are expected when backend WebSocket server isn't running
    // Connection failures should NOT break the app or show errors to users
    
    console.warn('⏸️ WebSocket connection issue');
    console.warn('💡 Notification system is using offline mode (localStorage only)');
    console.warn('💡 Real-time notifications will be available when WebSocket server is running at ws://localhost:8081/notifications');
    
    // IMPORTANT: Do NOT trigger onErrorRef for connection failures
    // The app should continue working normally even if WebSocket fails to connect
    // Only trigger error callback for actual runtime errors, not connection failures
    return;
  }, []);

  // Debug logging for connection state
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔌 useUniversalNotifications State:', {
        enabled,
        hasToken: !!token,
        userId,
        willConnect: enabled && !!token,
      });
    }
  }, [enabled, token, userId]);

  // Connect to WebSocket - only when enabled
  // The useWebSocket hook will handle connecting/disconnecting based on enabled flag
  const { connected, send, close } = useWebSocket(
    'notifications', // Channel path
    {
      onMessage: handleMessage,
      onConnect: handleConnect,
      onDisconnect: handleDisconnect,
      onError: handleError,
      filter: null, // Admins see everything, no facility filter
      autoReconnect: enabled, // Only auto-reconnect if enabled
      maxReconnectAttempts: 10,
      reconnectDelay: 3000,
      enabled: enabled, // Pass enabled flag to control connection
    }
  );

  // Send authentication message when connected and enabled
  useEffect(() => {
    if (connected && enabled && token) {
      console.log('🔐 Authenticating WebSocket connection...');
      console.log('📋 Token available:', !!token);
      console.log('👤 User ID:', userId);
      
      send(JSON.stringify({
        type: 'auth',
        token: `Bearer ${token}`,
        user_id: userId,
        facility_id: null, // Admin sees everything
        app: 'admin',
      }));
    }
  }, [connected, enabled, token, userId, send]);

  // Mark notification as read
  const markAsReadFn = useCallback(async (id) => {
    const updated = markAsRead(id);
    if (updated) {
      setNotifications(prev =>
        prev.map(n => n.id === id ? updated : n)
      );
      const newCount = getUnreadCount();
      setUnreadCount(newCount);
      saveUnreadCount(newCount); // Save to localStorage
    }
  }, [saveUnreadCount]);

  // Mark all notifications as read
  const markAllAsReadFn = useCallback(async () => {
    const updatedNotifications = notifications.map(n => ({
      ...n,
      isRead: true,
      read: true, // Keep for backward compatibility
      readAt: new Date().toISOString(),
    }));

    // Update storage
    markAllAsRead();

    // Update state
    setNotifications(updatedNotifications);
    setUnreadCount(0);
    saveUnreadCount(0); // Save to localStorage
  }, [notifications, saveUnreadCount]);

  // Clear all notifications
  const clearNotificationsFn = useCallback(async () => {
    const allNotifications = getNotifications();

    // Mark all as read first
    allNotifications.forEach(n => {
      markAsRead(n.id);
    });

    // Clear from storage using notificationStorage module
    // Note: This is a workaround - ideally we'd have a clearAll function
    const cleared = allNotifications.filter(n => !n.read);
    setNotifications([]);
    setUnreadCount(0);
    saveUnreadCount(0); // Save to localStorage
  }, [saveUnreadCount]);

  // Refresh notifications from storage
  const refreshNotificationsFn = useCallback(() => {
    const stored = getNotifications();
    setNotifications(stored);
    return stored;
  }, []);

  return {
    connected,
    unreadCount,
    notifications,
    markAsRead: markAsReadFn,
    markAllAsRead: markAllAsReadFn,
    clearNotifications: clearNotificationsFn,
    refreshNotifications: refreshNotificationsFn,
  };
}

/**
 * Play notification sound
 */
function playNotificationSound(soundFile) {
  try {
    // Try to play sound from assets
    const audio = new Audio(`/assets/${soundFile}`);
    audio.volume = 0.5;
    audio.play().catch(error => {
      console.warn('Failed to play notification sound:', error);
    });
  } catch (error) {
    console.warn('Error playing notification sound:', error);
  }
}

/**
 * Hook for simpler usage (auto-detects token and userId)
 * Note: This is a simplified version - use useUniversalNotifications with explicit options
 */
export function useUniversalNotificationsAuto() {
  // This hook is kept for backward compatibility but should not be used
  // Use useUniversalNotifications with proper options instead
  
  return {
    connected: false,
    unreadCount: 0,
    notifications: [],
    markAsRead: async () => {},
    markAllAsRead: async () => {},
    clearNotifications: async () => {},
    refreshNotifications: () => [],
  };
}

export default useUniversalNotifications;
